import { CATEGORY_GLYPHS, ITEMS, getItemDef } from './items'
import { lt } from './localized'
import type {
  ItemCategory,
  ItemStack,
  LocalizedText,
  RecipeDef,
  RecipeIngredient,
} from './types'

export const RECIPES: Record<string, RecipeDef> = {
  'kitchen-crackers-meal': {
    id: 'kitchen-crackers-meal',
    workstationId: 'kitchen',
    name: lt('Beschuit met beleg', 'Crispbread meal'),
    blurb: lt(
      'Genoeg droogbeschuit om een pan vol te maken. Beter dan rauw.',
      'Enough crispbread to fill a pan. Better than eating it dry.',
    ),
    glyph: '🍞',
    inputs: [{ defId: 'food-crackers', quantity: 2 }],
    output: { type: 'item', defId: 'food-bread', quantity: 1 },
    durationMs: 20_000,
    unlockAt: 0,
    skillXp: { skill: 'cooking', amount: 14 },
  },
  'kitchen-stamppot': {
    id: 'kitchen-stamppot',
    workstationId: 'kitchen',
    name: lt('Stamppot opwarmen', 'Reheat stamppot'),
    blurb: lt(
      'Oud brood en blikken gehaktballen worden iets dat op eten lijkt.',
      'Old bread and canned meatballs become something close to a meal.',
    ),
    glyph: '🍲',
    inputs: [
      { defId: 'food-bread', quantity: 1 },
      { defId: 'food-canned-meatballs', quantity: 1 },
    ],
    output: { type: 'item', defId: 'food-stamppot', quantity: 2 },
    durationMs: 30_000,
    unlockAt: 0,
    skillXp: { skill: 'cooking', amount: 22 },
  },
  'well-purify': {
    id: 'well-purify',
    workstationId: 'well',
    name: lt('Zuiver regenwater', 'Purify rainwater'),
    blurb: lt(
      'Filter twee jerrycans en hou een verzegelde fles over.',
      'Filter two rain jerrycans into one sealed bottle.',
    ),
    glyph: '💧',
    inputs: [{ defId: 'water-rain-jerrycan', quantity: 2 }],
    output: { type: 'item', defId: 'water-mineral-sealed', quantity: 1 },
    durationMs: 45_000,
    unlockAt: 0,
    skillXp: { skill: 'survival', amount: 12 },
  },
  'infirmary-field-kit': {
    id: 'infirmary-field-kit',
    workstationId: 'infirmary',
    name: lt('Veldhospitaal-kit', 'Field hospital kit'),
    blurb: lt(
      'Paracetamol en verband samenpakken voor noodgevallen.',
      'Bundle paracetamol and bandages for emergencies.',
    ),
    glyph: '⚕',
    inputs: [
      { defId: 'meds-paracetamol', quantity: 1 },
      { defId: 'meds-bandages', quantity: 1 },
    ],
    output: { type: 'item', defId: 'meds-field-kit', quantity: 1 },
    durationMs: 60_000,
    unlockAt: 8,
    skillXp: { skill: 'first_aid', amount: 28 },
  },
  'workshop-tools': {
    id: 'workshop-tools',
    workstationId: 'workshop',
    name: lt('Maak gereedschap', 'Build tools'),
    blurb: lt(
      'Roestig schroot omzetten in bruikbaar gereedschap.',
      'Turn rusty scrap into a usable toolkit.',
    ),
    glyph: '🔧',
    inputs: [{ defId: 'scrap-rusty', quantity: 3 }],
    output: { type: 'item', defId: 'scrap-toolkit', quantity: 1 },
    durationMs: 45_000,
    unlockAt: 0,
    skillXp: { skill: 'tinkering', amount: 16 },
  },
  'workshop-wire-tools': {
    id: 'workshop-wire-tools',
    workstationId: 'workshop',
    name: lt('Improviseer gereedschap', 'Improvise tools'),
    blurb: lt(
      'Draad en schroot samenknopen tot iets bruikbaars.',
      'Wire and scrap bound together into something useful.',
    ),
    glyph: '🪛',
    inputs: [
      { defId: 'scrap-wire', quantity: 3 },
      { defId: 'scrap-rusty', quantity: 1 },
    ],
    output: { type: 'item', defId: 'scrap-toolkit', quantity: 1 },
    durationMs: 40_000,
    unlockAt: 0,
    skillXp: { skill: 'tinkering', amount: 18 },
  },
  'workshop-radio': {
    id: 'workshop-radio',
    workstationId: 'workshop',
    name: lt('Bouw radio', 'Build radio'),
    blurb: lt(
      'Elektronica en gereedschap inruilen voor een werkende veldradio.',
      'Combine electronics and tools into a working field radio.',
    ),
    glyph: '📻',
    inputs: [
      { defId: 'scrap-electronics', quantity: 1 },
      { defId: 'scrap-toolkit', quantity: 1 },
    ],
    output: { type: 'item', defId: 'scrap-radio', quantity: 1 },
    durationMs: 90_000,
    unlockAt: 8,
    minWorkstationLevel: 2,
    skillXp: { skill: 'electronics', amount: 36 },
  },
  'workshop-radio-repair': {
    id: 'workshop-radio-repair',
    workstationId: 'workshop',
    name: lt('Herstel autoradio', 'Repair broken radio'),
    blurb: lt(
      'Gebroken unit openmaken, draad trekken, print vastzetten — een veldradio.',
      'Crack the wreck open, wire it up—salvage a field radio.',
    ),
    glyph: '📻',
    inputs: [
      { defId: 'scrap-radio-broken', quantity: 1 },
      { defId: 'scrap-wire', quantity: 2 },
      { defId: 'scrap-electronics', quantity: 1 },
    ],
    output: { type: 'item', defId: 'scrap-radio', quantity: 1 },
    durationMs: 75_000,
    unlockAt: 0,
    minWorkstationLevel: 1,
    skillXp: { skill: 'electronics', amount: 28 },
  },
}

export const RECIPE_LIST = Object.values(RECIPES)

/** Title + icon for crafting UI: the produced item, not the recipe action label. */
export function getCraftRecipeListDisplay(recipe: RecipeDef): {
  glyph: string
  name: LocalizedText
  outputQuantity: number | null
} {
  if (recipe.output.type === 'item') {
    const def = getItemDef(recipe.output.defId)
    const qty = recipe.output.quantity
    if (def) {
      return {
        glyph: def.glyph ?? CATEGORY_GLYPHS[def.category],
        name: def.name,
        outputQuantity: qty,
      }
    }
    return { glyph: recipe.glyph, name: recipe.name, outputQuantity: qty }
  }
  return { glyph: recipe.glyph, name: recipe.name, outputQuantity: null }
}

function countStack(inventory: ItemStack[], defId: string): number {
  return inventory.find((stack) => stack.defId === defId)?.quantity ?? 0
}

function countCategory(inventory: ItemStack[], category: ItemCategory): number {
  return inventory.reduce((sum, stack) => {
    const def = ITEMS[stack.defId]
    if (!def || def.category !== category) return sum
    return sum + stack.quantity
  }, 0)
}

export function ingredientOwned(
  inventory: ItemStack[],
  ingredient: RecipeIngredient,
): number {
  if ('defId' in ingredient) return countStack(inventory, ingredient.defId)
  return countCategory(inventory, ingredient.category)
}

export function canCraftRecipe(inventory: ItemStack[], recipe: RecipeDef): boolean {
  return recipe.inputs.every((ingredient) => {
    const owned = ingredientOwned(inventory, ingredient)
    return owned >= ingredient.quantity
  })
}
